import { Controller } from "@hotwired/stimulus"

// Kontroler do budowania schematu bazy Noe::Db
// Obsługuje zarówno single-table jak i multi-table schemas
export default class extends Controller {
  static targets = ["tablesContainer", "schemaField", "modeToggle", "addTableBtn"]
  static values = {
    fieldTypes: { type: Array, default: ["string", "text", "integer", "number", "boolean", "datetime", "date", "time"] },
    // teksty z locale (noe/db.schema_builder), ustawiane w noe/dbs/_form
    i18n: { type: Object, default: {} },
  }

  // Tekst z locale, wyescapowany do wstawienia w HTML; brak klucza daje sam klucz
  t(key) {
    return this.escapeHtml(this.i18nValue[key] || key)
  }

  connect() {
    this.loadSchemaToFields()
  }

  // Ładuje istniejący schemat
  loadSchemaToFields() {
    try {
      const schema = JSON.parse(this.schemaFieldTarget.value || "{}")

      if (schema.tables) {
        // Multi-table mode
        this.setMultiTableMode(true)
        Object.entries(schema.tables).forEach(([tableName, tableSchema]) => {
          this.addTableSection(tableName, tableSchema)
        })
      } else if (schema.fields && Object.keys(schema.fields).length > 0) {
        // Single-table mode z polami
        this.setMultiTableMode(false)
        this.addTableSection("", schema)
      } else {
        // Pusty schemat - single-table mode
        this.setMultiTableMode(false)
        this.addTableSection("", { fields: {}, indexed_fields: [] })
      }
    } catch (_e) {
      this.setMultiTableMode(false)
      this.addTableSection("", { fields: {}, indexed_fields: [] })
    }
  }

  // Ustawia tryb multi-table
  setMultiTableMode(isMulti) {
    this.isMultiTable = isMulti
    if (this.hasModeToggleTarget) {
      this.modeToggleTarget.checked = isMulti
    }
    if (this.hasAddTableBtnTarget) {
      this.addTableBtnTarget.style.display = isMulti ? "" : "none"
    }
  }

  // Toggle między single-table a multi-table
  toggleMode() {
    const isMulti = this.modeToggleTarget.checked
    this.isMultiTable = isMulti

    if (this.hasAddTableBtnTarget) {
      this.addTableBtnTarget.style.display = isMulti ? "" : "none"
    }

    // Aktualizuj widoczność pól table_name
    const tableNameInputs = this.tablesContainerTarget.querySelectorAll("[data-table-name-wrapper]")
    tableNameInputs.forEach((wrapper) => {
      wrapper.classList.toggle("hidden", !isMulti)
    })

    this.updateSchema()
  }

  // Dodaje nową tabelę
  addTable() {
    this.addTableSection("", { fields: {}, indexed_fields: [] })
    this.updateSchema()

    // Focus na nazwie tabeli
    const inputs = this.tablesContainerTarget.querySelectorAll("[data-field='tableName']")
    if (inputs.length > 0) {
      inputs[inputs.length - 1].focus()
    }
  }

  // Dodaje sekcję tabeli
  addTableSection(tableName, tableSchema) {
    const section = document.createElement("div")
    section.className = "border border-gray-200 rounded-lg mb-4"
    section.dataset.schemaBuilderTable = ""

    const fields = tableSchema.fields || {}
    const indexedFields = tableSchema.indexed_fields || []
    const uniqueFields = tableSchema.unique_fields || []

    section.innerHTML = `
      <div class="bg-gray-50 px-4 py-3 rounded-t-lg flex items-center justify-between gap-3">
        <div data-table-name-wrapper class="flex-1 ${this.isMultiTable ? "" : "hidden"}">
          <input type="text" data-field="tableName" value="${this.escapeHtml(tableName)}"
            placeholder="${this.t("table_name")}"
            data-action="input->schema-builder#updateSchema"
            class="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 text-sm font-mono" />
        </div>
        <div class="flex items-center gap-2">
          <button type="button" data-action="click->schema-builder#addFieldToTable"
            class="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            ${this.t("add_field")}
          </button>
          ${
            this.isMultiTable
              ? `
          <button type="button" data-action="click->schema-builder#removeTable"
            class="p-1 text-gray-400 hover:text-red-500" title="${this.t("remove_table")}">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          `
              : ""
          }
        </div>
      </div>
      <div data-fields-container class="px-4 py-3 space-y-2"></div>
    `

    this.tablesContainerTarget.appendChild(section)

    // Dodaj pola
    const fieldsContainer = section.querySelector("[data-fields-container]")
    Object.entries(fields).forEach(([fieldName, fieldDef]) => {
      const normalizedDef = typeof fieldDef === "string" ? { type: fieldDef } : fieldDef
      const isIndexed = indexedFields.includes(fieldName)
      this.addFieldRow(fieldsContainer, fieldName, normalizedDef, isIndexed, uniqueFields.includes(fieldName))
    })
  }

  // Usuwa tabelę
  removeTable(event) {
    const table = event.target.closest("[data-schema-builder-table]")
    if (table) {
      table.remove()
      this.updateSchema()
    }
  }

  // Dodaje pole do konkretnej tabeli
  addFieldToTable(event) {
    const table = event.target.closest("[data-schema-builder-table]")
    const fieldsContainer = table.querySelector("[data-fields-container]")
    this.addFieldRow(fieldsContainer, "", { type: "string" }, false, false)
    this.updateSchema()

    // Focus na nowym polu
    const inputs = fieldsContainer.querySelectorAll("[data-field='name']")
    if (inputs.length > 0) {
      inputs[inputs.length - 1].focus()
    }
  }

  // Dodaje wiersz pola
  addFieldRow(container, name, fieldDef, isIndexed, isUnique = false) {
    const row = document.createElement("div")
    row.className = "flex items-center gap-2 p-2 bg-white rounded border border-gray-100"
    row.dataset.schemaBuilderRow = ""

    row.innerHTML = `
      <input type="text" data-field="name" value="${this.escapeHtml(name)}"
        placeholder="${this.t("field_name")}"
        data-action="input->schema-builder#updateSchema"
        class="flex-1 min-w-0 rounded border-0 py-1 px-2 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 font-mono" />

      <select data-field="type" data-action="change->schema-builder#updateSchema"
        class="rounded border-0 py-1 px-2 text-sm text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600">
        ${this.fieldTypesValue.map((t) => `<option value="${t}" ${t === fieldDef.type ? "selected" : ""}>${t}</option>`).join("")}
      </select>

      <label class="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap" title="${this.t("indexed_hint")}">
        <input type="checkbox" data-field="indexed" ${isIndexed ? "checked" : ""}
          data-action="change->schema-builder#updateSchema"
          class="h-3.5 w-3.5 rounded border-gray-300 text-blue-600" />
        ${this.t("indexed")}
      </label>

      <label class="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap" title="${this.t("unique_hint")}">
        <input type="checkbox" data-field="unique" ${isUnique ? "checked" : ""}
          data-action="change->schema-builder#updateSchema"
          class="h-3.5 w-3.5 rounded border-gray-300 text-blue-600" />
        ${this.t("unique")}
      </label>

      <label class="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap" title="${this.t("required_hint")}">
        <input type="checkbox" data-field="required" ${fieldDef.required ? "checked" : ""}
          data-action="change->schema-builder#updateSchema"
          class="h-3.5 w-3.5 rounded border-gray-300 text-blue-600" />
        ${this.t("required")}
      </label>

      <button type="button" data-action="click->schema-builder#removeField"
        class="p-1 text-gray-400 hover:text-red-500" title="${this.t("remove_field")}">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `

    container.appendChild(row)
  }

  // Usuwa pole
  removeField(event) {
    const row = event.target.closest("[data-schema-builder-row]")
    if (row) {
      row.remove()
      this.updateSchema()
    }
  }

  // Aktualizuje schema JSON
  updateSchema() {
    const tables = this.tablesContainerTarget.querySelectorAll("[data-schema-builder-table]")

    if (this.isMultiTable) {
      // Multi-table format
      const schema = { tables: {} }

      tables.forEach((table) => {
        const tableName = table.querySelector("[data-field='tableName']")?.value?.trim()
        if (!tableName) return

        const tableSchema = this.extractTableSchema(table)
        schema.tables[tableName] = tableSchema
      })

      this.schemaFieldTarget.value = JSON.stringify(schema, null, 2)
    } else {
      // Single-table format
      const table = tables[0]
      if (table) {
        const schema = this.extractTableSchema(table)
        this.schemaFieldTarget.value = JSON.stringify(schema, null, 2)
      }
    }
  }

  // Wyciąga schemat z sekcji tabeli
  extractTableSchema(table) {
    const schema = { fields: {}, indexed_fields: [] }
    const rows = table.querySelectorAll("[data-schema-builder-row]")

    rows.forEach((row) => {
      const name = row.querySelector("[data-field='name']")?.value?.trim()
      if (!name) return

      const type = row.querySelector("[data-field='type']")?.value || "string"
      const required = row.querySelector("[data-field='required']")?.checked || false
      const indexed = row.querySelector("[data-field='indexed']")?.checked || false
      const unique = row.querySelector("[data-field='unique']")?.checked || false

      if (required) {
        schema.fields[name] = { type, required: true }
      } else {
        schema.fields[name] = type
      }

      if (indexed) {
        schema.indexed_fields.push(name)
      }

      if (unique) {
        schema.unique_fields = schema.unique_fields || []
        schema.unique_fields.push(name)
      }
    })

    return schema
  }

  // Escapuje tekst do HTML, także cudzysłowy - wynik trafia do atrybutów (value, title, placeholder)
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
